import React from 'react'
import {
  Dashboard as DashboardIcon,
  FactCheck as ExceptionsIcon,
  Build as BuildIcon,
  Folder as FolderIcon,
  List as ListIcon,
  Receipt as SalesIcon,
  Inventory as ProductsIcon,
  Link as LinkIcon,
  Inventory2 as Inventory2Icon,
  AccountBalance as AccountBalanceIcon,
  HealthAndSafety as HealthAndSafetyIcon,
} from '@mui/icons-material'
import { exceptionDomainDropdownItems } from '../components/SystemIcons'
import { userCanAccessInspectorItem, type FpUserAccessKind } from './fpUserAccess'

export interface FpUserAccessRef {
  kind: FpUserAccessKind
  id: string
}

export interface DropdownItem {
  label: string
  path: string
  icon: React.ReactElement
  dividerBefore?: boolean
  fpUserAccess?: FpUserAccessRef
}

export interface NavigationItem {
  label: string
  path: string
  icon: React.ReactElement
  type: 'simple' | 'dropdown' | 'resources_dropdown'
  dropdownItems?: DropdownItem[]
  evoOnly?: boolean
  fpUserAccess?: FpUserAccessRef
}

/**
 * Build the inspector nav items. Pass `ctx.country` so the Exceptions
 * dropdown can hide market-restricted domains (e.g. `client` is US-only).
 * Without ctx, no market gating is applied - older callers keep working
 * as before.
 */
export const buildInspectorNavigationItems = (
  ctx?: { country?: string },
): NavigationItem[] => [
  {
    label: 'Dashboard',
    path: '',
    icon: React.createElement(DashboardIcon),
    type: 'simple'
  },
  {
    label: 'Exceptions',
    path: 'exceptions',
    icon: React.createElement(ExceptionsIcon),
    type: 'dropdown',
    dropdownItems: [
      ...exceptionDomainDropdownItems('outlined', ctx),
      { label: 'All Exceptions', path: 'exceptions', icon: React.createElement(ListIcon), dividerBefore: true },
    ],
  },
  {
    label: 'Tools',
    path: 'tools',
    icon: React.createElement(BuildIcon),
    type: 'dropdown',
    dropdownItems: [
      { label: 'COGS Account Review', path: 'tools/cogs-review', icon: React.createElement(AccountBalanceIcon), fpUserAccess: { kind: 'tool', id: 'cogs_review' } },
      { label: 'Health Check', path: 'health', icon: React.createElement(HealthAndSafetyIcon), fpUserAccess: { kind: 'page', id: 'health_check' } },
      { label: 'Product Catalogue Review', path: 'tools/product-catalogue-review', icon: React.createElement(Inventory2Icon), fpUserAccess: { kind: 'tool', id: 'product_catalogue_review' } },
      { label: 'All Tools', path: 'tools', icon: React.createElement(ListIcon), dividerBefore: true },
    ]
  },
  {
    label: 'Data Explorer',
    path: 'data',
    icon: React.createElement(FolderIcon),
    type: 'dropdown',
    dropdownItems: [
      { label: 'Sales', path: 'data/sales', icon: React.createElement(SalesIcon), fpUserAccess: { kind: 'data', id: 'sales' } },
      { label: 'Products', path: 'data/products', icon: React.createElement(ProductsIcon), fpUserAccess: { kind: 'data', id: 'products' } },
      { label: 'Clients', path: 'data/clients', icon: React.createElement(ListIcon), fpUserAccess: { kind: 'data', id: 'clients' } },
    ]
  },
  {
    label: 'Links',
    path: 'links',
    icon: React.createElement(LinkIcon),
    type: 'resources_dropdown'
  }
]

/**
 * Internal-only - DO NOT RENDER. This list is computed once at module
 * load with no FP context, so it always contains every domain (including
 * market-restricted ones like `client`). Used solely by the path-matching
 * helpers below (`getNavigationPathType`, `getCurrentActiveIndex`) which
 * need the unfiltered list to resolve "what section am I in" regardless
 * of FP. For rendering nav items, always call
 * `buildInspectorNavigationItems({ country })`.
 */
export const inspectorNavigationItems: NavigationItem[] = buildInspectorNavigationItems()

/**
 * Filter top-level + dropdown nav items against the FP_USER access registry.
 * Staff roles are returned unchanged. Dropdowns with no remaining useful
 * children are dropped (an "All X" link alone is treated as useless).
 */
export function filterNavItemsForUser(
  items: NavigationItem[],
  role: string | undefined,
): NavigationItem[] {
  return items
    .filter((item) => {
      if (!item.fpUserAccess) return true
      return userCanAccessInspectorItem(role, item.fpUserAccess.kind, item.fpUserAccess.id)
    })
    .map((item) => {
      if (item.type !== 'dropdown' || !item.dropdownItems || role !== 'FP_USER') {
        return item
      }
      const filteredItems = item.dropdownItems.filter((child) => {
        if (!child.fpUserAccess) return true
        return userCanAccessInspectorItem(role, child.fpUserAccess.kind, child.fpUserAccess.id)
      })
      return { ...item, dropdownItems: filteredItems }
    })
    .filter((item) => {
      if (item.type !== 'dropdown') return true
      const remaining = item.dropdownItems ?? []
      const hasUsefulItems = remaining.some((child) => child.fpUserAccess || child.path !== item.path)
      return remaining.length > 0 && hasUsefulItems
    })
}

export const getNavigationPathType = (currentPath: string, basePath: string): 'exact' | 'section' | 'none' => {
  const relativePath = currentPath.replace(basePath, '').replace(/^\//, '')
  
  if (!relativePath) return 'exact' // Exact match for base path
  
  // Check if we're in a section (matches any dropdown item)
  for (const item of inspectorNavigationItems) {
    if (item.type === 'dropdown' && item.dropdownItems) {
      for (const dropdownItem of item.dropdownItems) {
        if (dropdownItem.path === relativePath || currentPath.endsWith(`/${dropdownItem.path}`)) {
          if (item.path && currentPath.includes(item.path)) {
            return 'section'
          }
        }
      }
    }
  }
  
  return 'none'
}

export const getCurrentActiveIndex = (currentPath: string, basePath: string): number => {
  const relativePath = currentPath.replace(basePath, '').replace(/^\//, '')
  
  if (!relativePath) return 0 // Dashboard
  
  // Check for exact path matches first (simple menu items)
  let activeIndex = inspectorNavigationItems.findIndex(item => item.type === 'simple' && item.path === relativePath)
  if (activeIndex >= 0) return activeIndex
  
  // Check for dropdown item matches or nested paths under them
  activeIndex = inspectorNavigationItems.findIndex(item => {
    if (item.type === 'dropdown' && item.dropdownItems) {
      return item.dropdownItems.some(dropdownItem => {
        // Exact match
        if (dropdownItem.path === relativePath || currentPath.endsWith(`/${dropdownItem.path}`)) {
          return true
        }
        
        // Check if current path starts with the dropdown item path (for nested routes)
        const dropdownBasePath = dropdownItem.path
        if (relativePath.startsWith(dropdownBasePath + '/')) {
          return true
        }
        
        return false
      })
    }
    return false
  })
  
  // If no dropdown item matched, check if the path belongs to a section
  if (activeIndex < 0) {
    activeIndex = inspectorNavigationItems.findIndex(item => {
      // For dropdown sections, check if the current path starts with the section path
      if (item.type === 'dropdown' && item.path) {
        // Check if relativePath starts with the section path
        // This handles any route under the section like products/inventory-balance, products/inventory, etc.
        return relativePath === item.path || relativePath.startsWith(item.path + '/')
      }
      return false
    })
  }
  
  return activeIndex >= 0 ? activeIndex : 0
}

export const isInSection = (currentPath: string, sectionPath: string): boolean => {
  // Check if current path starts with section path
  if (sectionPath === '') {
    // Dashboard section - only exact match
    return currentPath === '' || currentPath === '/'
  }
  
  return currentPath.includes(`/${sectionPath}/`) || currentPath.endsWith(`/${sectionPath}`)
}